import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  getInitialMeasure,
  isFunctionAttribute,
  measureSchema,
  type IAttribute,
  type IAutomation,
} from '../../../Interfaces/ILegalTransactionMeasure';
import { Dispatch, SetStateAction, useRef, useState } from 'react';
import { Add } from '@mui/icons-material';
import { useAlert } from '../../../context/AlertContext';
import { AttributeCard } from '../Attributes/AttributeCard';
import { CreateOrUpdateAttributeDialog } from '../Attributes/CreateOrUpdateAttributeDialog';
import { CreateOrUpdateAutomationDialog } from '../Automations/CreateOrUpdateAutomationDialog';
import type { ILegalRequirementTemplate } from '../../../Interfaces/ILegalRequirementTemplate';
import { type ILegalRequirement } from '../../../Interfaces/ILegalRequirement';
import type { IMeasure } from '../../../Interfaces/ILegalTransactionMeasure';
import { AutomationCard } from '../Automations/AutomationCard';
import { LoadingSpinner } from '../../../core/LoadingSpinner';
import { ErrorScreen } from '../../../core/ErrorScreen';
import { ILegalTransaction_has_LegalTransactionSpecialFunction } from '../../../Interfaces/ILegalTransaction_has_LegalTransactionSpecialFunction';
import { ILegalTransactionGwGFullObject } from '../../../Interfaces/ILegalTransactionGwG';
import {
  useGetLegalTransactionProgresses,
  useGetLegalTransactionStates,
} from '../../../hooks/reactQuerys/LegalTransactionQueries';
import { useGetLegalRequirementStates } from '../../../hooks/reactQuerys/LegalRequirementQueries';

type IProps = {
  legalRequirements: ILegalRequirement[];
  legalRequirementTemplates: ILegalRequirementTemplate[];
  initialMeasure?: IMeasure;
  specialFunctions?: ILegalTransaction_has_LegalTransactionSpecialFunction[];
  setSpecialFunctions?: Dispatch<
    SetStateAction<ILegalTransaction_has_LegalTransactionSpecialFunction[]>
  >;
  gwgs?: ILegalTransactionGwGFullObject[];
  setGwgs?: Dispatch<SetStateAction<ILegalTransactionGwGFullObject[]>>;
  onClose: () => void;
  onCreateOrUpdateMeasure: (measure: IMeasure) => void;
};

export const CreateOrUpdateMeasureDialog = ({
  legalRequirements,
  legalRequirementTemplates,
  initialMeasure,
  specialFunctions = [],
  setSpecialFunctions,
  gwgs = [],
  setGwgs,
  onClose,
  onCreateOrUpdateMeasure,
}: IProps) => {
  const theme = useTheme();
  const { showAlert } = useAlert();

  const { legalRequirementStates, isLoadingLegalRequirementStates } =
    useGetLegalRequirementStates();

  const { legalTransactionProgresses, isLoadingLegalTransactionProgresses } =
    useGetLegalTransactionProgresses();

  const { legalTransactionStates, isLoadingLegalTransactionStates } =
    useGetLegalTransactionStates();

  const [measure, setMeasure] = useState<IMeasure>(
    initialMeasure || getInitialMeasure()
  );
  const [
    showCreateOrUpdateAttributeDialog,
    setShowCreateOrUpdateAttributeDialog,
  ] = useState(false);
  const [
    showCreateOrUpdateAutomationDialog,
    setShowCreateOrUpdateAutomationDialog,
  ] = useState(false);

  const [editedAttribute, setEditedAttribute] = useState<IAttribute | null>(
    null
  );
  const [editedAutomation, setEditedAutomation] = useState<IAutomation | null>(
    null
  );

  const nameInputRef = useRef<HTMLInputElement | null>(null);

  const handleAddMeasure = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      measureSchema.parse(measure);
      onCreateOrUpdateMeasure(measure);
    } catch (error) {
      console.error(error);
      showAlert({
        text: 'Bitte füllen Sie alle Felder korrekt aus.',
        severity: 'error',
      });
    }
  };

  const handleAddAttribute = (attribute: IAttribute) => {
    setMeasure({ ...measure, attributes: [...measure.attributes, attribute] });
    setShowCreateOrUpdateAttributeDialog(false);
  };

  const handleMoveAttributeDown = (attribute: IAttribute) => {
    const index = measure.attributes.indexOf(attribute);
    const newAttributes = [...measure.attributes];
    newAttributes.splice(index, 1);
    newAttributes.splice(index + 1, 0, attribute);
    setMeasure({ ...measure, attributes: newAttributes });
  };

  const handleMoveAttributeUp = (attribute: IAttribute) => {
    const index = measure.attributes.indexOf(attribute);
    const newAttributes = [...measure.attributes];
    newAttributes.splice(index, 1);
    newAttributes.splice(index - 1, 0, attribute);
    setMeasure({ ...measure, attributes: newAttributes });
  };

  const handleDeleteAttribute = (attributeId: number) => {
    const attribute = measure.attributes.find(
      a => a.idMeasurementAttribute === attributeId
    );
    if (
      attribute &&
      isFunctionAttribute(attribute) &&
      setSpecialFunctions &&
      setGwgs
    ) {
      if (attribute.idSF === 2) {
        setGwgs(gwgs.filter(g => g.idLegalTransactionGwG !== attribute.Value));
      } else {
        setSpecialFunctions(
          specialFunctions.filter(
            sf =>
              sf.idLegalTransaction_has_LegalTransactionSpecialFunction !==
              attribute.Value
          )
        );
      }
    }

    setMeasure({
      ...measure,
      attributes: measure.attributes.filter(
        a => a.idMeasurementAttribute !== attributeId
      ),
    });
  };

  const handleUpdateAttribute = (attribute: IAttribute) => {
    setMeasure({
      ...measure,
      attributes: measure.attributes.map(a =>
        a.idMeasurementAttribute === attribute.idMeasurementAttribute
          ? attribute
          : a
      ),
    });
    setEditedAttribute(null);
  };

  const handleAddAutomation = (automation: IAutomation) => {
    setMeasure({
      ...measure,
      automations: [...(measure.automations || []), automation],
    });
    setShowCreateOrUpdateAutomationDialog(false);
  };

  const handleUpdateAutomation = (automation: IAutomation) => {
    setMeasure({
      ...measure,
      automations: (measure.automations || []).map(a =>
        a.idAutomation === automation.idAutomation ? automation : a
      ),
    });
    setEditedAutomation(null);
  };

  const handleDeleteAutomation = (automationId: number) => {
    setMeasure({
      ...measure,
      automations: (measure.automations || []).filter(
        a => a.idAutomation !== automationId
      ),
    });
  };

  if (
    isLoadingLegalRequirementStates ||
    isLoadingLegalTransactionProgresses ||
    isLoadingLegalTransactionStates
  ) {
    return (
      <Dialog open onClose={onClose} fullWidth maxWidth='md'>
        <LoadingSpinner height='60vh' />
      </Dialog>
    );
  }

  if (
    !legalRequirementStates ||
    !legalTransactionProgresses ||
    !legalTransactionStates
  ) {
    return (
      <Dialog open onClose={onClose} fullWidth maxWidth='md'>
        <ErrorScreen height='60vh' />
      </Dialog>
    );
  }

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        fullWidth
        maxWidth='md'
        TransitionProps={{
          onEntered: () => nameInputRef.current?.focus(),
        }}
      >
        <form onSubmit={handleAddMeasure}>
          <DialogTitle>
            Vorgangsmanager-Position{' '}
            {initialMeasure ? 'bearbeiten' : 'hinzufügen'}
          </DialogTitle>
          <DialogContent sx={{ minHeight: '60vh' }}>
            <TextField
              sx={{ mt: 1 }}
              label='Name'
              fullWidth
              value={measure.Name}
              onChange={e => setMeasure({ ...measure, Name: e.target.value })}
              inputRef={nameInputRef}
            />

            <Box
              mt={4}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant='h6'>Attribute</Typography>
              <IconButton
                onClick={() => setShowCreateOrUpdateAttributeDialog(true)}
              >
                <Add />
              </IconButton>
            </Box>
            <Box mt={1}>
              {measure.attributes.length === 0 ? (
                <Box
                  sx={{
                    height: '25vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.grey[50],
                    borderRadius: 4,
                    gap: 2,
                    p: 2,
                  }}
                >
                  <Typography variant='h6'>
                    Keine Attribute vorhanden
                  </Typography>
                  <Typography variant='body1'>
                    Für diese Vorgangsmanager-Position sind noch keine Attribute
                    vorhanden.
                  </Typography>
                  <Button
                    variant='contained'
                    color='primary'
                    onClick={() => setShowCreateOrUpdateAttributeDialog(true)}
                  >
                    Attribut hinzufügen
                  </Button>
                </Box>
              ) : (
                <Box display='flex' flexDirection='column' gap={1}>
                  {measure.attributes.map((attribute, index) => (
                    <AttributeCard
                      key={attribute.idMeasurementAttribute}
                      attribute={attribute}
                      onMoveDown={handleMoveAttributeDown}
                      onMoveUp={handleMoveAttributeUp}
                      onEdit={() => setEditedAttribute(attribute)}
                      onDelete={handleDeleteAttribute}
                      isLast={index === measure.attributes.length - 1}
                      isFirst={index === 0}
                    />
                  ))}
                </Box>
              )}
            </Box>
            <Box
              mt={4}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant='h6'>Automatisierungen</Typography>
              <IconButton
                onClick={() => setShowCreateOrUpdateAutomationDialog(true)}
              >
                <Add />
              </IconButton>
            </Box>
            <Box mt={1}>
              {!measure.automations || measure.automations.length === 0 ? (
                <Box
                  sx={{
                    height: '25vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.grey[50],
                    borderRadius: 4,
                    gap: 2,
                    p: 2,
                  }}
                >
                  <Typography variant='h6'>
                    Keine Automatisierungen vorhanden
                  </Typography>
                  <Typography variant='body1'>
                    Für diese Vorgangsmanager-Position sind noch keine
                    Automatisierungen vorhanden. vorhanden.
                  </Typography>
                  <Button
                    variant='contained'
                    color='primary'
                    onClick={() => setShowCreateOrUpdateAutomationDialog(true)}
                  >
                    Automatisierung hinzufügen
                  </Button>
                </Box>
              ) : (
                <Box display='flex' flexDirection='column' gap={1}>
                  {measure.automations.map(automation => (
                    <AutomationCard
                      key={automation.idAutomation}
                      automation={automation}
                      legalRequirements={legalRequirements}
                      legalRequirementTemplates={legalRequirementTemplates}
                      legalRequirementStates={legalRequirementStates}
                      legalTransactionProgresses={legalTransactionProgresses}
                      legalTransactionStates={legalTransactionStates}
                      onDelete={handleDeleteAutomation}
                      onEdit={setEditedAutomation}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              position: 'sticky',
              bottom: 0,
              backgroundColor: 'background.paper',
              borderTop: 1,
              borderColor: 'divider',
              p: 2,
            }}
          >
            <Button onClick={onClose}>Abbrechen</Button>
            <Button variant='contained' color='primary' type='submit'>
              {initialMeasure ? 'Speichern' : 'Hinzufügen'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {editedAttribute && (
        <CreateOrUpdateAttributeDialog
          initialAttribute={editedAttribute}
          onClose={() => setEditedAttribute(null)}
          onCreateOrUpdateAttribute={handleUpdateAttribute}
        />
      )}

      {showCreateOrUpdateAttributeDialog && (
        <CreateOrUpdateAttributeDialog
          onClose={() => setShowCreateOrUpdateAttributeDialog(false)}
          onCreateOrUpdateAttribute={handleAddAttribute}
        />
      )}

      {showCreateOrUpdateAutomationDialog && (
        <CreateOrUpdateAutomationDialog
          legalRequirements={legalRequirements}
          legalRequirementTemplates={legalRequirementTemplates}
          onClose={() => setShowCreateOrUpdateAutomationDialog(false)}
          onCreateOrUpdateAutomation={handleAddAutomation}
        />
      )}

      {editedAutomation && (
        <CreateOrUpdateAutomationDialog
          legalRequirements={legalRequirements}
          legalRequirementTemplates={legalRequirementTemplates}
          initialAutomation={editedAutomation}
          onClose={() => setEditedAutomation(null)}
          onCreateOrUpdateAutomation={handleUpdateAutomation}
        />
      )}
    </>
  );
};
